/* global dataLayer, fbq */

import Cookies from 'js-cookie';
import { createContext, useContext, useEffect, useRef } from 'react';
import ReactGA4 from 'react-ga4';

// HOCs.
import withLocation from '../../HOCs/withLocation';
// Constants.
import { USER_STATE_PENDING } from '../UserSessionContext';
// Contexts.
import { UserSessionContext } from '../UserSessionContext';
import useCookieHandling from './hooks/useCookieHandling';
// Hooks.
import useLandingDataLocalStorageHandling, { POSSIBLE_DATA_KEYS } from './hooks/useLandingDataLocalStorageHandling';

const AnalyticsContext = createContext();

const GA_TRACKING_ID = process.env.NODE_ENV === `production` ? 'G-GWYH93Y0H0' : 'G-TBJCCPXRFB';

const reactGaOptions = {
	gaOptions: {
		cookieDomain: `.sequencing.com`,
	},
};

if (process.env.NODE_ENV !== `production`) {
	// Uncomment the next line for debugging Google Analytics:
	// reactGaOptions.gaOptions.debug = true;
}

const AnalyticsProvider = withLocation((props) => {
	const user = useContext(UserSessionContext);

	const queue = useRef([]);

	const initReactGA = () => {
		if (!ReactGA4.isInitialized) ReactGA4.initialize(GA_TRACKING_ID, reactGaOptions);

		if (!queue.current || !queue.current.length) {
			return;
		}

		// Process queue and delete it.
		queue.current.forEach((func) => {
			func();
		});
		queue.current = [];
	};

	const queueOrDoFuncs = (funcs) => {
		if (queue.current) {
			queue.current.push(...funcs);
		} else {
			funcs.forEach((func) => func());
		}
	};

	useEffect(() => {
		if (!user.data) {
			return;
		}

		if (user.data.state !== USER_STATE_PENDING) {
			if (user.data.hashedUserId) {
				reactGaOptions.gaOptions.userId = user.data.hashedUserId;
			}

			initReactGA();
		}
	}, [user.data, user.data.hashedUserId, user.data.state]);

	useEffect(() => {
		const cookies = Cookies.get();
		Object.keys(cookies).forEach((key) => {
			if (key.slice(0, 14) !== `seq_datalayer_`) {
				return;
			}
			try {
				const value = JSON.parse(cookies[key]);
				dataLayer.push({ event: value.type, ...value.data });
				Cookies.remove(key);
			} catch {
				// It's fine. A cookie might not be JSON, or dataLayer might not
				// exist (like on development environment).
			}
		});
	}, []);

	useLandingDataLocalStorageHandling(props.location);
	useCookieHandling(props.location);

	return (
		<AnalyticsContext.Provider
			value={{
				pageview: () => {
					queueOrDoFuncs([
						() => ReactGA4.send('pageview'),
						() => {
							if (typeof fbq !== `function`) {
								return;
							}
							fbq(`track`, `PageView`);
						},
					]);
				},
				pushPlansAppsNoThanks: () => {
					queueOrDoFuncs([
						() =>
							ReactGA4.event({
								category: `React: Plans`,
								action: `"No thanks, purchase each app separately" clicked`,
							}),
						() => {
							if (typeof fbq !== `function`) {
								return;
							}
							fbq(`trackCustom`, `ReactPlansAppsNoThanks`);
						},
					]);
				},

				pushPlansMonthlyChanged: (value) => {
					queueOrDoFuncs([
						() =>
							ReactGA4.event({
								category: `React: Plans`,
								action: `Monthly payment toggle ${value ? `enabled` : `disabled`}`,
							}),
						() => {
							if (typeof fbq !== `function`) {
								return;
							}
							fbq(`trackCustom`, `ReactPlansMonthlyChanged`, {
								toggle_value: value,
							});
						},
					]);
				},
			}}
		>
			{props.children}
		</AnalyticsContext.Provider>
	);
});

export { AnalyticsContext, AnalyticsProvider, POSSIBLE_DATA_KEYS as LANDING_DATA_KEYS };
